import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';

import { BasicSignalIcon } from '@/components/Icons/BasicSignalIcon';
import { AMIcon } from '@/components/Icons/AMIcon';
import { FMIcon } from '@/components/Icons/FMIcon';
import { PMIcon } from '@/components/Icons/PMIcon';
import { AMRandomIcon } from '@/components/Icons/AMRandomIcon';
import { CompareIcon } from '@/components/Icons/CompareIcon';
import { SoundEncodingIcon } from '@/components/Icons/SoundEncodingIcon';

const fundamentalSections = [
    { title: 'Базовые Сигналы', description: 'Исследуйте основные формы сигналов: синусоиду, косинусоиду, прямоугольный и треугольный.', link: '/signal', icon: BasicSignalIcon },
    { title: 'Амплитудная Модуляция', description: 'Изучите принципы амплитудной модуляции сигнала и влияние различных параметров на результат.', link: '/am', icon: AMIcon },
    { title: 'Частотная Модуляция', description: 'Исследуйте, как меняется частота несущего сигнала под влиянием модулирующего сигнала.', link: '/fm', icon: FMIcon },
    { title: 'Фазовая Модуляция', description: 'Изучите принципы фазовой модуляции и её применение в цифровой передаче данных.', link: '/pm', icon: PMIcon },
];

const advancedSections = [
    { title: 'Кодирование звука (АЦП)', description: 'Интерактивное исследование шагов аналого-цифрового преобразования: дискретизация, квантование и кодирование.', link: '/sound-encoding', icon: SoundEncodingIcon },
    { title: 'АМ со случайным сигналом', description: 'Исследуйте амплитудную модуляцию с использованием случайного шумового сигнала.', link: '/am-random', icon: AMRandomIcon },
    { title: 'Сравнение типов модуляции', description: 'Сравните различные типы модуляции с одинаковыми входными параметрами в одном интерфейсе.', link: '/compare', icon: CompareIcon },
];

const SectionCard: React.FC<{ section: typeof fundamentalSections[0], index: number }> = ({ section, index }) => {
    const Icon = section.icon;
    return (
        <motion.div
            className="bg-bg-card rounded-xl shadow-lg relative overflow-hidden group flex flex-col"
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.5, delay: index * 0.1 }}
            whileHover={{ y: -8, boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)' }}
        >
            <div className="p-8 flex flex-col h-full">
                <div className="w-16 h-16 rounded-full flex items-center justify-center bg-secondary/10 text-secondary mb-6 shadow-sm">
                    <Icon className="w-9 h-9" />
                </div>
                <h3 className="text-2xl font-bold text-text-primary mb-3">{section.title}</h3>
                <p className="text-text-secondary mb-6 flex-grow">{section.description}</p>
                <Link to={section.link} className="inline-block text-center font-semibold text-white px-6 py-3 rounded-lg bg-secondary hover:bg-primary transition-colors duration-300 mt-auto shadow-md">
                    Открыть демонстрацию
                </Link>
            </div>
        </motion.div>
    );
};

export const HomePage: React.FC = () => {
    return (
        <div className="space-y-16">
            <div>
                <h2 className="text-3xl font-bold text-text-primary mb-2 text-center">Основы теории сигналов и модуляции</h2>
                <p className="text-lg text-text-secondary text-center mb-8">Фундаментальные концепции, лежащие в основе телекоммуникаций.</p>
                <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
                    {fundamentalSections.map((section, index) => (
                        <SectionCard key={section.title} section={section} index={index} />
                    ))}
                </div>
            </div>
            <div>
                <h2 className="text-3xl font-bold text-text-primary mb-2 text-center">Цифровая обработка и передача данных</h2>
                <p className="text-lg text-text-secondary text-center mb-8">Прикладные и более сложные темы, включая преобразование сигналов.</p>
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                    {advancedSections.map((section, index) => (
                        <SectionCard key={section.title} section={section} index={index} />
                    ))}
                </div>
            </div>
        </div>
    );
};